import {
	BinderAmendment,
	MultiSelectOption,
	ParamConditionType,
	ParamConditionValueDetails,
	SubscriptionParamsDetails,
} from "@dune/types";
import {
	Input,
	MultipleSelector,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@dune/ui";
import { formatCurrencyPrecise, getActionTypeLabel } from "@dune/utils";
import { comparisonToStringMap } from "@dune/vars";
import { useDepartmentsContext } from "../../../context/DepartmentsContext";

const BinderSubscriptionParams = ({
	binderAmendment,
}: {
	binderAmendment: BinderAmendment;
}) => {
	const binderSubscriptionParams = binderAmendment.binderSubscriptionParams;
	if (!binderSubscriptionParams) return null;

	return (
		<div className="flex flex-col gap-4 w-full">
			<h1 className="text-[#144C65] text-2xl font-medium">
				Limites de souscription
			</h1>
			<div className="flex flex-col gap-4 w-full">
				<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-6 gap-4">
					<div className="flex flex-row gap-6 w-full">
						<Input
							label="Automatique (coût de construction)"
							value={formatCurrencyPrecise(
								binderSubscriptionParams.automaticLimit,
							)}
							readOnly
						/>
						<Input
							label="Maximale (coût de construction)"
							value={formatCurrencyPrecise(
								binderSubscriptionParams.maximumLimit,
							)}
							readOnly
						/>
					</div>
				</div>
			</div>
			{binderSubscriptionParams.subscriptionParams &&
				(binderSubscriptionParams.subscriptionParams.length > 1 ? (
					<Tabs defaultValue="new-construction" className="w-full">
						<TabsList>
							<TabsTrigger value="new-construction">
								Construction neuve
							</TabsTrigger>
							<TabsTrigger value="renovation-extension">
								Rénovation/Extension
							</TabsTrigger>
						</TabsList>
						<TabsContent value="new-construction">
							<SubscriptionDetailsContainerByWarrantyName
								subscriptionParams={binderSubscriptionParams.subscriptionParams.filter(
									(subscriptionParam) =>
										subscriptionParam.constructionSiteType === "CONSTRUCTION",
								)}
							/>
						</TabsContent>
						<TabsContent value="renovation-extension">
							<SubscriptionDetailsContainerByWarrantyName
								subscriptionParams={binderSubscriptionParams.subscriptionParams.filter(
									(subscriptionParam) =>
										subscriptionParam.constructionSiteType ===
										"RENOVATION_OR_EXTENSION",
								)}
							/>
						</TabsContent>
					</Tabs>
				) : (
					<SubscriptionDetailsContainerByWarrantyName
						subscriptionParams={binderSubscriptionParams.subscriptionParams}
					/>
				))}
		</div>
	);
};
const SubscriptionDetailsContainerByWarrantyName = ({
	subscriptionParams,
}: {
	subscriptionParams: SubscriptionParamsDetails[];
}) => {
	const warrantyNames = [
		...new Set(
			subscriptionParams.map(
				(subscriptionParam) => subscriptionParam.warrantyName,
			),
		),
	];
	return (
		<div className="flex flex-col gap-4">
			{warrantyNames.map((name) => {
				return (
					<div key={name} className="flex flex-col gap-4">
						<h1 className="text-[#144C65] text-2xl font-medium">{name}</h1>
						<SubscriptionDetailsContainer
							subscriptionParam={subscriptionParams.find(
								(subscriptionParam) => subscriptionParam.warrantyName === name,
							)}
						/>
					</div>
				);
			})}
		</div>
	);
};

const SubscriptionDetailsContainer = ({
	subscriptionParam,
}: { subscriptionParam?: SubscriptionParamsDetails }) => {
	if (!subscriptionParam) return null;
	return (
		<div className="flex flex-col gap-4">
			{subscriptionParam.subscriptionParamGroups.map((paramGroup) => {
				return (
					<div
						key={paramGroup.subscriptionParamType}
						className="flex flex-col border-solid border-1 border-gray-200 rounded-lg w-full p-6 gap-6"
					>
						{paramGroup.params.map((param) => (
							<div key={param.paramKey} className="flex flex-col gap-2">
								{param.constructionSiteTypeExclusive && (
									<div className="flex flex-row gap-4">
										<h1 className="text-l font-medium italic">
											{`${getActionTypeLabel(param.constructionSiteTypeExclusive)} seulement`}
										</h1>
									</div>
								)}
								<div className="flex flex-row gap-4 w-full">
									{param.paramName && (
										<h1 className="text-[#144C65] text-l font-medium w-1/6">
											{param.paramName}
										</h1>
									)}
									<div className="flex flex-col gap-4 w-full">
										{param.paramConditionValues.map((condition) => (
											<div key={condition.id} className="flex flex-row gap-4">
												<ParamConditionRowContainer condition={condition} />
											</div>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				);
			})}
		</div>
	);
};

const ParamConditionRowContainer = ({
	condition,
}: { condition: ParamConditionValueDetails }) => {
	return (
		<div className="flex flex-row gap-4 w-full items-end">
			{condition.conditionTypes.map(
				(type: ParamConditionType, index: number) => {
					return (
						<ParamConditionContainer
							key={type}
							type={type}
							condition={condition}
							index={index}
						/>
					);
				},
			)}
		</div>
	);
};

const ParamConditionContainer = ({
	type,
	condition,
	index,
}: {
	type: ParamConditionType;
	condition: ParamConditionValueDetails;
	index: number;
}) => {
	const { departments } = useDepartmentsContext();

	if (type === "MANDATORY") {
		return (
			<Select defaultValue="MANDATORY" label="Condition">
				<SelectTrigger
					size="none"
					className="disabled:opacity-100 h-11 disabled:bg-gray-100"
					disabled
				>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="MANDATORY">Obligatoire</SelectItem>
				</SelectContent>
			</Select>
		);
	}

	const comparisonParamConditionTypes: ParamConditionType[] = [
		"FLOORS",
		"CONSTRUCTION_COST",
		"DAYS",
		"EXTENSION",
		"DEPARTMENTS",
		"HABITATIONS_COUNT",
	];
	if (comparisonParamConditionTypes.includes(type)) {
		const defaultComparison =
			type === "FLOORS"
				? condition.floorComparisonType
				: type === "CONSTRUCTION_COST"
					? condition.constructionCostComparisonType
					: type === "EXTENSION"
						? condition.extensionComparisonType
						: type === "DAYS"
							? condition.daysComparisonType
							: type === "HABITATIONS_COUNT"
								? condition.habitationsCountComparisonType
								: undefined;
		const inputValue =
			type === "FLOORS"
				? String(condition.floor)
				: type === "CONSTRUCTION_COST"
					? String(condition.constructionCost)
					: type === "EXTENSION"
						? String(condition.extension)
						: type === "DAYS"
							? String(condition.days)
							: type === "HABITATIONS_COUNT"
								? String(condition.habitationsCount)
								: "";
		const inputLabel =
			type === "FLOORS"
				? "Nb niveaux"
				: type === "CONSTRUCTION_COST"
					? "Coût de construction (€)"
					: type === "EXTENSION"
						? "Surface créée (m²)"
						: type === "DAYS"
							? "Nb de jour avant démarrage des travaux"
							: type === "HABITATIONS_COUNT"
								? "Nombre de logements"
								: "Territorialité";

		if (type === "DEPARTMENTS") {
			const selectedDepartments: MultiSelectOption[] =
				condition.departments.map((dept) => ({
					value: dept.department.code,
					label: dept.department.name ?? "",
				}));

			const departmentOptions: MultiSelectOption[] = departments.map(
				(dept) => ({
					value: dept.code,
					label: dept.name ?? "",
				}),
			);

			return (
				<div className="w-full">
					<div className="flex flex-row gap-4">
						<h1 className="text-l font-medium">Exclu si</h1>
					</div>
					<MultipleSelector
						label={inputLabel}
						value={selectedDepartments}
						disabled
						options={departmentOptions}
						placeholder="Rechercher et sélectionner des départements..."
						hidePlaceholderWhenSelected
						emptyIndicator={
							<p className="text-center text-sm text-gray-500">
								Aucun département trouvé
							</p>
						}
						mergeValueLabel
					/>
				</div>
			);
		}

		return (
			<>
				{defaultComparison && (
					<Select
						defaultValue={defaultComparison}
						label={type === "DAYS" ? "Date du jour" : "Condition"}
					>
						<SelectTrigger
							size="none"
							className="disabled:opacity-100 h-11 disabled:bg-gray-100"
							disabled
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{Object.entries(comparisonToStringMap).map(([key, value]) => {
								return (
									<SelectItem key={key} value={key}>
										{String(value)}
									</SelectItem>
								);
							})}
						</SelectContent>
					</Select>
				)}
				<Input label={inputLabel} value={inputValue} readOnly />
				{index !== condition.conditionTypes.length - 1 && (
					<Select defaultValue={condition.operator} label="ET/OU">
						<SelectTrigger
							size="none"
							className="disabled:opacity-100 h-11 disabled:bg-gray-100"
							disabled
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={"AND"}>ET</SelectItem>
							<SelectItem value={"OR"}>OU</SelectItem>
						</SelectContent>
					</Select>
				)}
			</>
		);
	}
	const bearingStructureAndUnderpinningParamConditionTypes: ParamConditionType[] =
		["BEARING_STRUCTURE", "UNDERPINNING"];
	if (bearingStructureAndUnderpinningParamConditionTypes.includes(type)) {
		const defaultValue =
			type === "BEARING_STRUCTURE"
				? condition.bearingStructure
				: condition.underpinning;
		const triggerLabel =
			type === "BEARING_STRUCTURE"
				? "Atteinte aux structures porteuses"
				: "Reprise en sous-oeuvre";
		return (
			<>
				<Select
					defaultValue={defaultValue ? "true" : "false"}
					label={triggerLabel}
				>
					<SelectTrigger
						size="none"
						className="disabled:opacity-100 h-11 disabled:bg-gray-100"
						disabled
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={"true"}>Oui</SelectItem>
						<SelectItem value={"false"}>Non</SelectItem>
					</SelectContent>
				</Select>
			</>
		);
	}

	if (type === "RGA") {
		return (
			<Select
				defaultValue={condition.rga === 2 ? "mod" : "str"}
				label="Risque de RGA"
			>
				<SelectTrigger
					size="none"
					className="disabled:opacity-100 h-11 disabled:bg-gray-100"
					disabled
				>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={"mod"}>Modéré</SelectItem>
					<SelectItem value={"str"}>Fort</SelectItem>
				</SelectContent>
			</Select>
		);
	}

	if (type === "SOCIAL_LANDLORD_RENTAL") {
		const defaultValue = condition.socialLandlordRental
		const triggerLabel = "Bailleur social"
		return (
			<>
				<Select
					defaultValue={defaultValue ? "true" : "false"}
					label={triggerLabel}
				>
					<SelectTrigger
						size="none"
						className="disabled:opacity-100 h-11 disabled:bg-gray-100"
						disabled
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={"true"}>Oui</SelectItem>
						<SelectItem value={"false"}>Non</SelectItem>
					</SelectContent>
				</Select>
			</>
		);
	}
};

export default BinderSubscriptionParams;
